import { useState, useEffect, type RefObject } from 'react';

/**
 * Hook to track horizontal scroll position and determine whether
 * left/right fade overlays should be shown.
 *
 * Used by horizontally-scrollable containers (CategoryPills, ProductsFilterBar)
 * to display gradient fade indicators at the edges.
 */
export const useScrollFade = (scrollRef: RefObject<HTMLElement>) => {
    const [showLeftFade, setShowLeftFade] = useState(false);
    const [showRightFade, setShowRightFade] = useState(false);

    useEffect(() => {
        const element = scrollRef.current;
        if (!element) return;

        const updateFadeState = () => {
            const { scrollLeft, scrollWidth, clientWidth } = element;
            const isAtStart = scrollLeft <= 1; // Small threshold for rounding
            const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 1;

            setShowLeftFade(!isAtStart);
            setShowRightFade(!isAtEnd);
        };

        // Initial check
        updateFadeState();

        // Listen to scroll events
        element.addEventListener('scroll', updateFadeState, { passive: true });

        // Listen to resize in case content changes
        const resizeObserver = new ResizeObserver(updateFadeState);
        resizeObserver.observe(element);

        return () => {
            element.removeEventListener('scroll', updateFadeState);
            resizeObserver.disconnect();
        };
    }, [scrollRef]);

    return { showLeftFade, showRightFade };
};
